import {
  FormEvent,
  MouseEvent,
  KeyboardEvent,
  PureComponent,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import ModalContent from '@components/Modal/components/ModalContent/ModalContent';
import { isPassiveSupport } from '@utilities/passiveSupport';

interface Props {
  id: string;
  title: ReactNode;
  asForm: boolean;
  isNaked: boolean;
  disabled: boolean;
  children: ReactNode;
  isLoading: boolean;
  hideSecondary: boolean;
  secondaryLabel: string;
  blurTargetId: string;
  primaryLabel: string;
  hidePrimary: boolean;
  hideFooter: boolean;
  hideHeader: boolean;
  hideClose: boolean;
  scrollable: boolean;
  onClose: (id: string) => void;
  onPrimaryClick?: (
    e: FormEvent | MouseEvent | KeyboardEvent,
    id: string
  ) => void;
  onSecondaryClick?: (id: string) => void;
  fullscreen: boolean;
  primaryLoading: boolean;
  secondaryLoading: boolean;
  primaryDisabled: boolean;
  secondaryDisabled: boolean;
  removeWindowScroll: boolean;
}

class ModalPortal extends PureComponent<Props> {
  private container: HTMLDivElement | null = null;

  private oldBodyOverflowY = 'auto';
  private oldScrollPos = 0;
  constructor(props: Props) {
    super(props);
    if (RUNTIME_ENV !== 'server' && typeof document !== 'undefined') {
      this.container = document.createElement('div');
      this.container.setAttribute('id', 'modal-container');
    }
  }
  handleKeyUp = (e) => {
    const { id, onClose, asForm, onPrimaryClick } = this.props;

    switch (e.keyCode) {
      case 13:
        if (!asForm) {
          onPrimaryClick && onPrimaryClick(e, id);
        }
        break;
      case 27:
        onClose && onClose(id);
        break;
      default:
        break;
    }
  };
  componentDidMount() {
    const { container } = this;
    const { removeWindowScroll } = this.props;
    if (
      RUNTIME_ENV !== 'server' &&
      typeof document !== 'undefined' &&
      container !== null
    ) {
      document.body.appendChild(container);
      if (removeWindowScroll) {
        this.oldBodyOverflowY = document.body.style['overflow-y'] || 'auto';
        this.oldScrollPos = window.pageYOffset;
        document.body.style['overflow-y'] = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.oldScrollPos}px`;
        document.body.style.width = '100%';
      }
      const passiveSupported = isPassiveSupport();
      const options = passiveSupported ? { passive: true } : false;
      document.addEventListener('keyup', this.handleKeyUp, options);
    }
  }

  componentWillUnmount() {
    const { container } = this;
    const { removeWindowScroll } = this.props;
    if (RUNTIME_ENV !== 'server' && typeof document !== 'undefined') {
      if (container !== null && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (error) {
          console.log(error);
        }
      }
      document.body.style['overflow-y'] = this.oldBodyOverflowY;
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      if (removeWindowScroll) window.scrollTo(0, this.oldScrollPos);
      document.removeEventListener('keyup', this.handleKeyUp);
    }
  }

  render() {
    const { container } = this;
    const {
      id,
      title,
      asForm,
      isNaked,
      onClose,
      disabled,
      children,
      isLoading,
      fullscreen,
      hideSecondary,
      secondaryLabel,
      blurTargetId,
      hidePrimary,
      primaryLabel,
      onPrimaryClick,
      onSecondaryClick,
      hideFooter,
      scrollable,
      hideHeader,
      hideClose,
      primaryLoading,
      secondaryLoading,
      primaryDisabled,
      secondaryDisabled,
    } = this.props;
    if (RUNTIME_ENV === 'server' && typeof document === 'undefined')
      return null;
    if (container === null) return null;
    return createPortal(
      <ModalContent
        id={id}
        title={title}
        asForm={asForm}
        isNaked={isNaked}
        scrollable={scrollable}
        disabled={disabled}
        isLoading={isLoading}
        fullscreen={fullscreen}
        hideSecondary={hideSecondary}
        blurTargetId={blurTargetId}
        secondaryLabel={secondaryLabel}
        hidePrimary={hidePrimary}
        primaryLabel={primaryLabel}
        onClose={onClose}
        onPrimaryClick={onPrimaryClick}
        onSecondaryClick={onSecondaryClick}
        hideClose={hideClose}
        primaryLoading={primaryLoading}
        secondaryLoading={secondaryLoading}
        primaryDisabled={primaryDisabled}
        secondaryDisabled={secondaryDisabled}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
      >
        {children}
      </ModalContent>,
      container
    );
  }
}

export default ModalPortal;
